import React, { useState, useEffect, useMemo, useRef } from "react";
import { FiRefreshCw, FiCheck, FiX } from "react-icons/fi";
import styles from "../styles/home.module.css";

const QuizSession = ({ 
  quizData, 
  onComplete, 
  onExit,
  onOpenInModal
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const containerRef = useRef(null);

  // Helpers
  const canonicalMap = {
    'js': 'javascript',
    'ecmascript': 'javascript',
  };
  // Basic normalization (no synonym collapsing) used for visual highlighting
  const basicNormalize = (v) => {
    const s = typeof v === 'string' ? v : String(v ?? '');
    return s.trim().toLowerCase().replace(/\s+/g, ' ');
  };
  // Canonical normalization (collapses synonyms) used for grading acceptance
  const normalizeValue = (v) => {
    const s = typeof v === 'string' ? v : String(v ?? '');
    const lower = s.trim().toLowerCase()
      .replace(/[\p{P}\p{S}]/gu, ' ') // remove punctuation/symbols
      .replace(/\s+/g, ' ')
      .trim();
    return canonicalMap[lower] ?? lower;
  };

  // Derive option list unconditionally to keep hooks order stable
  const optionList = useMemo(() => {
    const card = quizData?.cards?.[currentIndex];
    const raw = card?.options ?? card?.choices ?? card?.answers ?? card?.optionsList ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [quizData, currentIndex]);
  
  // Initialize user answers
  useEffect(() => {
    if (quizData && quizData.cards) {
      const initialAnswers = {};
      quizData.cards.forEach(card => {
        initialAnswers[card.id] = {
          answered: false,
          userAnswer: "",
          isCorrect: null
        };
      });
      setUserAnswers(initialAnswers);
    }
  }, [quizData]);

  // Keyboard shortcuts: Left/Right navigate, Enter submits
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(document.activeElement)) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (showFeedback) handleNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'Enter') {
        if (!showFeedback) handleSubmitAnswer();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [showFeedback, currentIndex, selectedOption, userAnswers]);

  const handleNext = () => {
    if (currentIndex < quizData.cards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedOption(null);
      setShowFeedback(false);
    } else {
      // Quiz completed
      setQuizCompleted(true);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      // Show previous selection if available
      const prevCard = quizData.cards[currentIndex - 1];
      const prevAnswer = userAnswers[prevCard.id];
      setSelectedOption(prevAnswer.userAnswer || null);
      setShowFeedback(prevAnswer.answered);
    }
  };
  
  const handleOptionSelect = (option) => {
    if (showFeedback) return; // Prevent changing answer after submission
    setSelectedOption(option);
  };
  
  const handleSubmitAnswer = () => {
    if (selectedOption === null || showFeedback) return;
    
    const currentCard = quizData.cards[currentIndex];
    // Normalize correct answers (string or array)
    const correctRaw = currentCard.answer ?? currentCard.correct ?? currentCard.correctAnswer ?? currentCard.answers;
    const correctList = Array.isArray(correctRaw) ? correctRaw : [correctRaw].filter(Boolean);
    const isCorrect = correctList.map(normalizeValue).includes(normalizeValue(selectedOption));
    
    // Update user answers
    setUserAnswers(prev => ({
      ...prev,
      [currentCard.id]: {
        answered: true,
        userAnswer: selectedOption,
        isCorrect
      }
    }));
    
    setShowFeedback(true);
    setShowExplanation(false);
  };

  const handleRestart = () => {
    setCurrentIndex(0);
    setSelectedOption(null);
    setShowFeedback(false);
    setQuizCompleted(false);
    
    // Reset user answers
    const resetAnswers = {};
    quizData.cards.forEach(card => {
      resetAnswers[card.id] = {
        answered: false,
        userAnswer: "",
        isCorrect: null
      };
    });
    setUserAnswers(resetAnswers);
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete(userAnswers);
    }
  };

  // Calculate quiz statistics
  const calculateStats = () => {
    const total = quizData.cards.length;
    const answered = Object.values(userAnswers).filter(a => a.answered).length;
    const correct = Object.values(userAnswers).filter(a => a.isCorrect).length;
    const accuracy = answered > 0 ? Math.round((correct / answered) * 100) : 0;
    
    return { total, answered, correct, accuracy };
  };

  if (!quizData || !quizData.cards || quizData.cards.length === 0) {
    return <div className={styles.errorMessage}>No quiz data available</div>;
  }

  if (quizCompleted) {
    const stats = calculateStats();
    
    return (
      <div className={styles.quizSummary}>
        <h3 className={styles.summaryHeader}>Quiz Complete!</h3>
        
        <div className={styles.summaryStats}>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{stats.total}</div>
            <div className={styles.statLabel}>Total Questions</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{stats.answered}</div>
            <div className={styles.statLabel}>Answered</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{stats.correct}</div>
            <div className={styles.statLabel}>Correct</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statValue}>{stats.accuracy}%</div>
            <div className={styles.statLabel}>Accuracy</div>
          </div>
        </div>
        
        <div className={styles.quizActionButtons}>
          <button 
            onClick={handleRestart} 
            className={`${styles.quizButton} ${styles.restartButton}`}
          >
            <FiRefreshCw /> Restart Quiz
          </button>
          <button 
            onClick={handleComplete} 
            className={`${styles.quizButton} ${styles.completeButton}`}
          >
            Return to Chat
          </button>
        </div>
      </div>
    );
  }
  
  // Get current question
  const currentCard = quizData.cards[currentIndex];
  const correctRawForCard = currentCard?.answer ?? currentCard?.correct ?? currentCard?.correctAnswer ?? currentCard?.answers;
  const correctListForCard = Array.isArray(correctRawForCard) ? correctRawForCard : [correctRawForCard].filter(Boolean);
  const isSelectedCorrect = showFeedback && correctListForCard.map(normalizeValue).includes(normalizeValue(selectedOption));
  // Sets for visual highlighting (basic) and canonical grading
  const correctBasicSet = new Set(correctListForCard.map(basicNormalize));
  const correctCanonicalSet = new Set(correctListForCard.map(normalizeValue));
  // Build display values prioritizing canonical-matched option texts
  let correctDisplayValues = correctListForCard;
  if (optionList && optionList.length) {
    // First, try canonical match to option texts
    const canonicalMatched = optionList.filter(opt => correctCanonicalSet.has(normalizeValue(opt)));
    if (canonicalMatched.length) {
      correctDisplayValues = canonicalMatched;
    } else {
      // Fallback to basic match
      const basicMatched = optionList.filter(opt => correctBasicSet.has(basicNormalize(opt)));
      if (basicMatched.length) correctDisplayValues = basicMatched;
    }
  }
  const stats = calculateStats();

  return (
    <div className={styles.quizSessionContainer} ref={containerRef}>
      <div className={styles.quizHeader}>
        <h3>{quizData.title}</h3>
        <div className={styles.progressMeta}>
          Q {currentIndex + 1}/{quizData.cards.length} • {stats.accuracy}%
          {onOpenInModal && (
            <button className={styles.linkButton} style={{ marginLeft: 8 }} onClick={() => onOpenInModal('quiz')}>
              Open in modal
            </button>
          )}
        </div>
      </div>
      
      <div className={styles.quizProgress}>
        <div className={styles.progressTrack}>
          <div 
            className={styles.progressFill} 
            style={{ width: `${((currentIndex + 1) / quizData.cards.length) * 100}%` }}
          />
        </div>
        <span className={styles.progressText}>Question {currentIndex + 1} of {quizData.cards.length}</span>
      </div>
      
      <div className={styles.quizContent}>
        <div className={styles.quizScrollable}>
          <h3 className={styles.quizQuestion}>{currentCard.question}</h3>
          
          <div className={styles.optionsContainer}>
            {optionList && optionList.length > 0 ? optionList.map((option, index) => (
              <div
                key={`option-${index}`}
                className={`${styles.optionItem} ${
                  selectedOption === option ? styles.selectedOption : ''
                } ${
                  showFeedback && (() => {
                    const correctRaw = currentCard.answer ?? currentCard.correct ?? currentCard.correctAnswer ?? currentCard.answers;
                    const correctList = Array.isArray(correctRaw) ? correctRaw : [correctRaw].filter(Boolean);
                    const correctSetBasic = new Set(correctList.map(basicNormalize));
                    return correctSetBasic.has(basicNormalize(option));
                  })() ? styles.correctOption : ''
                } ${
                  showFeedback && !isSelectedCorrect && selectedOption === option && (() => {
                    const correctRaw = currentCard.answer ?? currentCard.correct ?? currentCard.correctAnswer ?? currentCard.answers;
                    const correctList = Array.isArray(correctRaw) ? correctRaw : [correctRaw].filter(Boolean);
                    const correctSetBasic = new Set(correctList.map(basicNormalize));
                    return !correctSetBasic.has(basicNormalize(option));
                  })() ? styles.incorrectOption : ''
                }`}
                onClick={() => handleOptionSelect(option)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleOptionSelect(option);
                }}
              >
                <div className={styles.optionContent}>
                  <span className={styles.optionLetter}>{String.fromCharCode(65 + index)}</span>
                  <span className={styles.optionText}>{option}</span>
                </div>
                {/* Inline badge for correct/incorrect on each option when revealed */}
                {showFeedback && (() => {
                  const correctRaw = currentCard.answer ?? currentCard.correct ?? currentCard.correctAnswer ?? currentCard.answers;
                  const correctList = Array.isArray(correctRaw) ? correctRaw : [correctRaw].filter(Boolean);
                  const correctSetBasic = new Set(correctList.map(basicNormalize));
                  return correctSetBasic.has(basicNormalize(option));
                })() && (
                  <span className={`${styles.optionIcon} ${styles.optionBadge} ${styles.badgeCorrect}`}><FiCheck /></span>
                )}
                {showFeedback && !isSelectedCorrect && selectedOption === option && (() => {
                  const correctRaw = currentCard.answer ?? currentCard.correct ?? currentCard.correctAnswer ?? currentCard.answers;
                  const correctList = Array.isArray(correctRaw) ? correctRaw : [correctRaw].filter(Boolean);
                  const correctSetBasic = new Set(correctList.map(basicNormalize));
                  return !correctSetBasic.has(basicNormalize(option));
                })() && (
                  <span className={`${styles.optionIcon} ${styles.optionBadge} ${styles.badgeWrong}`}><FiX /></span>
                )}
              </div>
            )) : (
              <div className={styles.errorMessage}>No answer choices provided.</div>
            )}
          </div>
          
          {showFeedback && (
            <div className={`${styles.feedbackInline}`}>
              {isSelectedCorrect ? (
                <span className={styles.badgeCorrectMini}><FiCheck /> Correct</span>
              ) : (
                <>
                  <span className={styles.badgeWrongMini}><FiX /> Incorrect</span>
                  {correctDisplayValues && correctDisplayValues.length > 0 && (
                    <span className={styles.feedbackAnswer}>
                      Correct answer: {correctDisplayValues.join(', ')}
                    </span>
                  )}
                </>
              )}
              {currentCard.explanation && (
                <button className={styles.linkButton} onClick={() => setShowExplanation(v => !v)}>
                  {showExplanation ? 'Hide why' : 'Why?'}
                </button>
              )}
            </div>
          )}

          {showFeedback && currentCard.explanation && showExplanation && (
            <div className={styles.feedbackExplanationMini}>{currentCard.explanation}</div>
          )}
        </div>
        <div className={styles.quizNavigation}>
          <button 
            onClick={handlePrev} 
            disabled={currentIndex === 0}
            className={`${styles.navButton} ${currentIndex === 0 ? styles.disabled : ''}`}
          >
            Previous
          </button>
          
          {!showFeedback ? (
            <button 
              onClick={handleSubmitAnswer}
              disabled={selectedOption === null}
              className={`${styles.submitButton} ${selectedOption === null ? styles.disabled : ''}`}
            >
              Submit Answer
            </button>
          ) : (
            <button 
              onClick={handleNext}
              className={styles.navButton}
            >
              {currentIndex === quizData.cards.length - 1 ? 'Finish Quiz' : 'Next Question'}
            </button>
          )}

          <button 
            onClick={onExit} 
            className={styles.quizExitButton}
          >
            Exit
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizSession;
